import { GoogleAds, Modal } from '@sgcmpteltd/uikit-next';
import type {
  Props as GoogleAdsProps,
} from '@sgcmpteltd/uikit-next/components/google-ads';
import { useLatest } from '@sgcmpteltd/uikit-react';
import dayjs from 'dayjs';
import { useRef, useState } from 'react';

import {
  ptoPopup as ptoPopupGoogleAdsConfig,
} from '@/configs/google/ads/desktop';
import type { OnRemainSecondsChange } from '@/hooks/use-count-down';
import { useCountDown } from '@/hooks/use-count-down';

import {
  BACKDROP_TRANSITION_DURATION_MS,
  MODAL_CLOSE_COUNTDOWN_SEC,
} from './constants';
import styles from './styles.module.scss';
import type { Component, OnModalClose } from './types';

export const PtoAdModal: Component = ({
  hasCloseCountdown,
  isOpen,
  backdropTransitionDurationMs = BACKDROP_TRANSITION_DURATION_MS,
  onClose,
}) => {
  const [
    countdownTargetTimestamp,
    setCountdownTargetTimestamp,
  ] = useState<number | null>(null);

  if (isOpen) {
    if (hasCloseCountdown && countdownTargetTimestamp === null) {
      setCountdownTargetTimestamp(
        +dayjs().add(MODAL_CLOSE_COUNTDOWN_SEC, 'seconds'),
      );
    }

    if (!hasCloseCountdown && countdownTargetTimestamp !== 0) {
      setCountdownTargetTimestamp(0);
    }
  }

  const onModalClose: OnModalClose = () => {
    setCountdownTargetTimestamp(0);
    onClose?.();
  };

  const {
    ref: onRemainSecondsChangeRef,
  } = useLatest<OnRemainSecondsChange>({
    value: (remainSeconds) => {
      if (remainSeconds > 0 || !isOpen) {
        return;
      }

      onModalClose();
    },
  });

  const {
    remainSeconds: modalCloseRemainSeconds,
  } = useCountDown({
    targetTimestamp: countdownTargetTimestamp ?? 0,
    onRemainSecondsChangeRef,
  });

  const onPtoPopupAdSlotRenderEndedRef: GoogleAdsProps['onSlotRenderEndedRef'] = useRef(({ // @suppress longLineCheck
    isEmpty,
    slot: currentSlot,
  }) => {
    if (!isEmpty) {
      return;
    }

    window.googletag.cmd.push(() => {
      window.googletag.pubads().refresh([
        currentSlot,
      ]);
    });
  });

  if (!isOpen) {
    return (
      <Modal
        backdropTransitionDurationMs={backdropTransitionDurationMs}
        isOpen={isOpen}
        title={null}
        onClose={onModalClose} />
    );
  }

  return (
    <Modal
      backdropTransitionDurationMs={backdropTransitionDurationMs}
      className={styles.containerRoot}
      contentClassName={styles.containerModalContent}
      headerClassName={styles.containerModalHeader}
      headerControlClassName={styles.containerModalHeaderControl}
      headerControlPrefix={(
        typeof modalCloseRemainSeconds === 'number'
        && modalCloseRemainSeconds > 0
        && (
          <span
            className={styles.textModalCloseCountdown}>
            Closing Ad in...
            {modalCloseRemainSeconds}
          </span>
        )
      )}
      isOpen={isOpen}
      title={null}
      onClose={onModalClose}>
      <GoogleAds
        adUnitPath={ptoPopupGoogleAdsConfig.adUnitPath}
        divId={ptoPopupGoogleAdsConfig.divId}
        slot={ptoPopupGoogleAdsConfig.slot}
        onSlotRenderEndedRef={onPtoPopupAdSlotRenderEndedRef} />
    </Modal>
  );
};
